import type { RfqSourceInvite, RfqSourceRole, RfqSourceTargetKind } from '@console/types'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api.js'
import { useMe } from '../auth/queries.js'
import { formatAsOf } from '../format.js'
import {
  useConfirmRfqSourceInvite,
  useCreateRfqSourceInvite,
  useRevokeRfqSourceInvite,
  useRfqSourceInvites,
} from '../rfq-source-invites/queries.js'
import { Button, HairlineRule, Input, Select, Skeleton, Table } from '../ui/index.js'
import styles from './RfqSourceInvitesPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-035`, `SPEC-ONB-001 §3a/§3b`. Invite and revoke tenant-scoped RFQ
 * sources — the primary onboarding path.
 *
 * ── WHY "WHO IS THIS" IS ITS OWN STEP, NOT A ROLE DROPDOWN ─────────────────
 * `§3b`: "an external RFQ source is not a staff role with fewer permissions,
 * it is a different kind of party... a flat dropdown holding both is how an
 * admin authorises a customer while believing they authorised a colleague."
 * So `WhoPicker` below is a structural fork, not a `Select` option — picking
 * "outside the company" removes the staff role choice entirely rather than
 * narrowing it, and there is no path from one block to the other without
 * consciously switching.
 *
 * ── WHY GRANTING ADMIN NEEDS A SECOND CLICK ─────────────────────────────────
 * `§3b`: "granting an admin role is itself privileged... not a peer entry in
 * the list." Least privilege (`staffStandard`) is the picker's default and
 * the only way to reach `staffAdmin` is to also tick a confirmation this
 * component will not let the invite button ignore.
 * ═══════════════════════════════════════════════════════════════════════════
 */

type Who = 'staff' | 'external'

const STAFF_ROLE_OPTIONS = [
  { value: 'staffStandard', label: 'Standard staff access' },
  { value: 'staffAdmin', label: 'Full admin (can also invite and revoke)' },
]

const STATUS_LABEL: Record<RfqSourceInvite['status'], string> = {
  pending: 'Pending confirmation',
  active: 'Active',
  revoked: 'Revoked',
}

const ROLE_LABEL: Record<RfqSourceRole, string> = {
  staffAdmin: 'Staff — full admin',
  staffStandard: 'Staff — standard',
  externalRfqOnly: 'External — submit only',
}

function InviteForm({ tenantId }: { tenantId: string }) {
  const [who, setWho] = useState<Who>('staff')
  const [staffRole, setStaffRole] = useState<'staffStandard' | 'staffAdmin'>('staffStandard')
  const [adminConfirmed, setAdminConfirmed] = useState(false)
  const [targetKind, setTargetKind] = useState<RfqSourceTargetKind>('phone')
  const [targetValue, setTargetValue] = useState('')
  const create = useCreateRfqSourceInvite(tenantId)

  const role: RfqSourceRole = who === 'external' ? 'externalRfqOnly' : staffRole
  const needsAdminConfirm = who === 'staff' && staffRole === 'staffAdmin'
  const canSubmit = targetValue.trim() !== '' && (!needsAdminConfirm || adminConfirmed)

  function submit() {
    if (!canSubmit) return
    create.mutate(
      { target: { kind: targetKind, value: targetValue.trim() }, role },
      {
        onSuccess: () => {
          setTargetValue('')
          setAdminConfirmed(false)
        },
      },
    )
  }

  return (
    <section className={styles['inviteForm']}>
      <h2 className={styles['sectionTitle']}>Invite an RFQ source</h2>

      <div className={styles['whoGroup']} role="radiogroup" aria-label="Who is this">
        <label className={styles['whoOption']}>
          <input
            type="radio"
            name="who"
            checked={who === 'staff'}
            onChange={() => {
              setWho('staff')
            }}
          />
          Someone at your company
        </label>
        <label className={styles['whoOption']} data-external="true">
          <input
            type="radio"
            name="who"
            checked={who === 'external'}
            onChange={() => {
              setWho('external')
              setAdminConfirmed(false)
            }}
          />
          An outside RFQ source — not your staff, sees nothing back
        </label>
      </div>

      {who === 'staff' && (
        <>
          <Select
            label="Access level"
            options={STAFF_ROLE_OPTIONS}
            value={staffRole}
            onChange={(event) => {
              setStaffRole(event.target.value as 'staffStandard' | 'staffAdmin')
              setAdminConfirmed(false)
            }}
          />
          {needsAdminConfirm && (
            <label className={styles['adminConfirm']}>
              <input
                type="checkbox"
                checked={adminConfirmed}
                onChange={(event) => {
                  setAdminConfirmed(event.target.checked)
                }}
              />
              I understand this grants full admin access, including inviting and revoking others.
            </label>
          )}
        </>
      )}

      <div className={styles['targetRow']}>
        <Select
          label="Contact by"
          options={[
            { value: 'phone', label: 'Phone (WhatsApp)' },
            { value: 'email', label: 'Email' },
          ]}
          value={targetKind}
          onChange={(event) => {
            setTargetKind(event.target.value as RfqSourceTargetKind)
          }}
        />
        <Input
          label={targetKind === 'phone' ? 'Phone number' : 'Email address'}
          value={targetValue}
          onChange={(event) => {
            setTargetValue(event.target.value)
          }}
          placeholder={targetKind === 'phone' ? '+971...' : 'name@company.com'}
        />
      </div>

      {create.isError && (
        <p role="alert" className={styles['error']}>
          {create.error instanceof ApiError
            ? create.error.message
            : 'That invite could not be sent.'}
        </p>
      )}

      <Button disabled={!canSubmit || create.isPending} onClick={submit}>
        {create.isPending ? 'Sending…' : 'Send invite'}
      </Button>
      <p className={styles['hint']}>
        The invite is not active until the invited party confirms a code sent to them — never to
        this session.
      </p>
    </section>
  )
}

function ConfirmRow({ invite, tenantId }: { invite: RfqSourceInvite; tenantId: string }) {
  const [otp, setOtp] = useState('')
  const confirm = useConfirmRfqSourceInvite(tenantId)

  return (
    <div className={styles['confirmRow']}>
      <Input
        label="Confirmation code"
        value={otp}
        onChange={(event) => {
          setOtp(event.target.value)
        }}
        placeholder="Code sent to the invited party"
      />
      <Button
        variant="secondary"
        disabled={otp.trim() === '' || confirm.isPending}
        onClick={() => {
          confirm.mutate(
            { inviteId: invite.id, otp: otp.trim() },
            {
              onSuccess: () => {
                setOtp('')
              },
            },
          )
        }}
      >
        {confirm.isPending ? 'Confirming…' : 'Confirm'}
      </Button>
      {confirm.isError && (
        <p role="alert" className={styles['error']}>
          {confirm.error instanceof ApiError
            ? confirm.error.message
            : 'That code was not accepted.'}
        </p>
      )}
    </div>
  )
}

function RevokeButton({ invite, tenantId }: { invite: RfqSourceInvite; tenantId: string }) {
  const revoke = useRevokeRfqSourceInvite(tenantId)
  if (invite.status === 'revoked') return null

  return (
    <Button
      variant="secondary"
      disabled={revoke.isPending}
      onClick={() => {
        // Immediate and irreversible (`SPEC-ONB-001 §3a`) — one native
        // confirmation, no undo screen, matching how little ceremony a
        // destructive-but-correct action here should carry.
        if (!window.confirm(`Revoke this invite for ${invite.target.value}?`)) return
        revoke.mutate(invite.id)
      }}
    >
      {revoke.isPending ? 'Revoking…' : 'Revoke'}
    </Button>
  )
}

export function RfqSourceInvitesPage() {
  const me = useMe()
  const tenantId = me.data?.currentTenantId ?? ''
  const invites = useRfqSourceInvites(tenantId)

  if (me.isPending || (tenantId !== '' && invites.isPending)) return <Skeleton />
  if (tenantId === '') return <p role="alert">Not signed in.</p>

  return (
    <main className={styles['page']}>
      <header className={styles['header']}>
        <h1>RFQ source invites</h1>
        <Link to="/">← Home</Link>
      </header>

      <InviteForm tenantId={tenantId} />

      <HairlineRule />

      <h2 className={styles['sectionTitle']}>Invited sources — who, when, still active</h2>

      {invites.isError && <p role="alert">That invite list could not be loaded.</p>}

      {invites.data !== undefined && (
        <Table
          rowKey={(row: RfqSourceInvite) => row.id}
          columns={[
            {
              key: 'target',
              header: 'Contact',
              render: (row: RfqSourceInvite) => row.target.value,
            },
            {
              key: 'role',
              header: 'Access',
              render: (row: RfqSourceInvite) => ROLE_LABEL[row.role],
            },
            {
              key: 'status',
              header: 'Status',
              render: (row: RfqSourceInvite) => STATUS_LABEL[row.status],
            },
            {
              key: 'invited',
              header: 'Invited',
              // Operator-console UX audit finding — `toLocaleString()` read
              // the browser's own local time with no zone label, the same
              // `LGH-024`-class bug already found and fixed on `RunsPage`'s
              // Time column.
              render: (row: RfqSourceInvite) => formatAsOf(row.invitedAt),
            },
            {
              key: 'action',
              header: '',
              render: (row: RfqSourceInvite) => (
                // A still-pending invite can be revoked too — sent to the
                // wrong number is exactly the case `SPEC-ONB-001 §3a` names.
                <div className={styles['actions']}>
                  {row.status === 'pending' && <ConfirmRow invite={row} tenantId={tenantId} />}
                  <RevokeButton invite={row} tenantId={tenantId} />
                </div>
              ),
            },
          ]}
          rows={invites.data}
        />
      )}
    </main>
  )
}
